import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { FileText, Plus, Edit, Trash2, Eye } from "lucide-react";
import { useProviderWaivers } from "@/hooks/useProviderWaivers";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ScrollArea } from "@/components/ui/scroll-area";

interface WaiverManagementProps {
  providerId: string;
}

const WaiverManagement = ({ providerId }: WaiverManagementProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: waivers, isLoading } = useProviderWaivers(providerId);
  
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingWaiver, setEditingWaiver] = useState<any>(null);
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    is_active: true
  });

  const resetForm = () => {
    setFormData({
      title: "",
      content: "",
      is_active: true
    });
  };

  const handleCreateWaiver = async () => {
    try {
      if (!formData.title.trim() || !formData.content.trim()) {
        toast({
          title: "Error",
          description: "Please fill in all required fields",
          variant: "destructive",
        });
        return;
      }

      const { error } = await supabase
        .from('provider_waivers')
        .insert({
          provider_id: providerId,
          title: formData.title.trim(),
          content: formData.content.trim(),
          is_active: formData.is_active
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Waiver created successfully",
      });

      queryClient.invalidateQueries({
        queryKey: ["provider-waivers", providerId]
      });

      setIsAddDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error creating waiver:', error);
      toast({
        title: "Error",
        description: "Failed to create waiver",
        variant: "destructive",
      });
    }
  };

  const handleUpdateWaiver = async () => {
    try {
      if (!formData.title.trim() || !formData.content.trim()) {
        toast({
          title: "Error",
          description: "Please fill in all required fields",
          variant: "destructive",
        });
        return;
      }

      const { error } = await supabase
        .from('provider_waivers')
        .update({
          title: formData.title.trim(),
          content: formData.content.trim(),
          is_active: formData.is_active,
          updated_at: new Date().toISOString()
        })
        .eq('id', editingWaiver.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Waiver updated successfully",
      });

      queryClient.invalidateQueries({
        queryKey: ["provider-waivers", providerId]
      });

      setIsEditDialogOpen(false);
      setEditingWaiver(null);
      resetForm();
    } catch (error) {
      console.error('Error updating waiver:', error);
      toast({
        title: "Error",
        description: "Failed to update waiver",
        variant: "destructive",
      });
    }
  };

  const handleDeleteWaiver = async (waiverId: string) => {
    try {
      const { error } = await supabase
        .from('provider_waivers')
        .delete()
        .eq('id', waiverId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Waiver deleted successfully",
      });

      queryClient.invalidateQueries({
        queryKey: ["provider-waivers", providerId]
      });
    } catch (error) {
      console.error('Error deleting waiver:', error);
      toast({
        title: "Error",
        description: "Failed to delete waiver",
        variant: "destructive",
      });
    }
  };

  const handleToggleStatus = async (waiverId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('provider_waivers')
        .update({
          is_active: !currentStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', waiverId);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Waiver ${!currentStatus ? 'activated' : 'deactivated'} successfully`,
      });

      queryClient.invalidateQueries({
        queryKey: ["provider-waivers", providerId]
      });
    } catch (error) {
      console.error('Error updating waiver status:', error);
      toast({
        title: "Error",
        description: "Failed to update waiver status",
        variant: "destructive",
      });
    }
  };

  const openEditDialog = (waiver: any) => {
    setEditingWaiver(waiver);
    setFormData({
      title: waiver.title,
      content: waiver.content,
      is_active: waiver.is_active
    });
    setIsEditDialogOpen(true);
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FileText className="h-5 w-5" />
            <span>Waiver Management</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-20 bg-gray-200 rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="space-y-2">
            <CardTitle className="flex items-center space-x-2 text-lg sm:text-xl">
              <FileText className="h-5 w-5" />
              <span>Waiver Management</span>
            </CardTitle>
            <p className="text-sm text-gray-600">
              Manage waivers that customers must agree to before booking your services
            </p>
          </div>
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={resetForm} className="w-full sm:w-auto">
                <Plus className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Add Waiver</span>
                <span className="sm:hidden">Add</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create New Waiver</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Enter waiver title"
                  />
                </div>
                <div>
                  <Label htmlFor="content">Content *</Label>
                  <Textarea
                    id="content"
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    placeholder="Enter waiver content and terms"
                    rows={8}
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                  <Label htmlFor="is_active">Active</Label>
                </div>
                <div className="flex flex-col sm:flex-row justify-end gap-2">
                  <Button variant="outline" onClick={() => setIsAddDialogOpen(false)} className="w-full sm:w-auto">
                    Cancel
                  </Button>
                  <Button onClick={handleCreateWaiver} className="w-full sm:w-auto">
                    Create Waiver
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {waivers && waivers.length > 0 ? (
          <div className="space-y-4">
            {waivers.map((waiver) => (
              <Card key={waiver.id} className="border-l-4 border-l-blue-500">
                <CardContent className="p-3 sm:p-4">
                  <div className="space-y-3">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                          <h4 className="font-semibold text-base sm:text-lg truncate">{waiver.title}</h4>
                          <Badge 
                            variant={waiver.is_active ? "default" : "secondary"}
                            className="w-fit"
                          >
                            {waiver.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </div>
                        <div className="text-sm text-gray-600 mb-3">
                          <ScrollArea className="h-16 sm:h-20 w-full">
                            <p className="whitespace-pre-wrap pr-2">{waiver.content}</p>
                          </ScrollArea>
                        </div>
                        <div className="text-xs text-gray-500">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1">
                            <span>Created: {new Date(waiver.created_at).toLocaleDateString()}</span>
                            {waiver.updated_at !== waiver.created_at && (
                              <span className="sm:ml-2 sm:before:content-['•'] sm:before:mr-2">
                                Updated: {new Date(waiver.updated_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Actions Row */}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 pt-2 border-t">
                      <div className="flex items-center gap-2 flex-1">
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button variant="outline" size="sm" className="flex-1 sm:flex-none">
                              <Eye className="h-4 w-4 mr-1" />
                              <span className="sm:hidden">View</span>
                              <span className="hidden sm:inline">Preview</span>
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                              <DialogTitle>{waiver.title}</DialogTitle>
                            </DialogHeader>
                            <ScrollArea className="max-h-96 w-full">
                              <div className="text-sm whitespace-pre-wrap p-4 bg-gray-50 rounded">
                                {waiver.content}
                              </div>
                            </ScrollArea>
                          </DialogContent>
                        </Dialog>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditDialog(waiver)}
                          className="flex-1 sm:flex-none"
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          <span>Edit</span>
                        </Button>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <div className="flex items-center space-x-2">
                          <Switch
                            checked={waiver.is_active}
                            onCheckedChange={() => handleToggleStatus(waiver.id, waiver.is_active)}
                          />
                          <span className="text-sm text-gray-600">
                            {waiver.is_active ? "Active" : "Inactive"}
                          </span>
                        </div>
                        
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Waiver</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete "{waiver.title}"? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteWaiver(waiver.id)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p className="font-medium">No waivers created yet</p>
            <p className="text-sm mt-2 px-4">Create your first waiver to require customer agreements before booking</p>
          </div>
        )}

        {/* Edit Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Waiver</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-title">Title *</Label>
                <Input
                  id="edit-title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Enter waiver title"
                />
              </div>
              <div>
                <Label htmlFor="edit-content">Content *</Label>
                <Textarea
                  id="edit-content"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Enter waiver content and terms"
                  rows={8}
                />
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="edit-is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label htmlFor="edit-is_active">Active</Label>
              </div>
              <div className="flex flex-col sm:flex-row justify-end gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setIsEditDialogOpen(false);
                    setEditingWaiver(null);
                    resetForm();
                  }}
                  className="w-full sm:w-auto"
                >
                  Cancel
                </Button>
                <Button onClick={handleUpdateWaiver} className="w-full sm:w-auto">
                  Update Waiver
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default WaiverManagement;
